/**
 * jobResource — the fulfillment-job dispatch list, authored against the
 * resource-kit v2 model/view contracts and consumed directly as a
 * `ResourceBundle` by `ResourcePage` (#1805 slice A4b). The
 * six record-shaped cell projections, the `copyable` affordance and the
 * order-label helpers they share moved to `./widgets.tsx`; `fields[].render`
 * and `fields[].copyable` have no legacy carrier on the v2 side.
 *
 * `capabilities.search: true` is NOT decoration. `/api/fulfillment-jobs` really
 * searches (order number / Onfleet short id) and
 * `services/__tests__/resource-searchable-parity.test.ts` binds this resource to
 * the generated `FULFILLMENT_JOB_SEARCH`, which is non-empty — dropping the
 * opt-in deletes a working box AND turns that table red. The rule is
 * two-directional and the test asserts both halves: a resource whose route
 * publishes no `x-searchable` columns must omit the key entirely, because
 * claiming a box the route rejects 400s the operator's first keystroke.
 *
 * `auditResourceType: 'fulfillment_jobs'` rides the MODEL, not the host: it
 * names the ledger row this record writes to, the bridge emits the key only
 * when the model declares it, and its one consumer is the change-history tab
 * the `ledgerId` gate in `packages/resource-kit-ui/react/form/FormView.tsx`
 * derives from it. Forgetting it loses that tab silently, with every other
 * surface still rendering.
 *
 * `detail.statusField` and `detail.title` ride the HOST. Neither is a cell: the
 * lifecycle badge is page-header chrome `ResourceDetailPanel` renders beside
 * the title, and no cell widget can reach that slot (see
 * `packages/resource-kit-controllers/react/bundle.ts`).
 */
import {
  useToday,
  type DataTableParams,
  type ResourceBundle,
} from '@resource-kit/controllers/react';
import { Badge, LIST_TRUNCATE } from '@resource-kit/ui/react';
import {
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
  type Condition,
} from '@resource-kit/model';
import { useQuery } from '@tanstack/react-query';
import { http } from '@lib/api';
import { toListResult, type ListPage } from '@/services/list-envelope';
import { type ListResult, shortenId, serializeConditionsToParams } from '@resource-kit/controllers';
import { driverAPI } from '@/services/drivers';
import { toApiListParams } from '@/services/list-request';
import { FULFILLMENT_JOB_FILTERS, FULFILLMENT_JOB_SORTS } from '@/services/filters.generated';
import i18n from '@/i18n';
import { lookupEnumLabelOrWarn } from '@/lib/enum-label';
import { PERMISSIONS } from '@/permissions';
import { queryKeys } from '@lib/queryKeys';
import CutoffBanner from './packaging/CutoffBanner';
import { TodayDeliveryCard } from './components/TodayDeliveryCard';
import { DeliveryProgress } from './components/DeliveryProgress';
import { PACKAGING_PRINT_PAGE, buildPackagingSheet } from './packaging/packagingPrint';
import { JobActionsToolbar, isOnfleetAssignedConflict } from './components/JobActionsCard';
import { formatJobOrderLabel } from './widgets';
import { renderJobCard } from './list/jobCard';
import { STAGE_JOB_STATUSES, type BoardStage } from './board/boardModel';
import { addCalendarDays } from '@lib/calendar-day';

// The fulfillment-job row type is the generated OpenAPI component
// (`components['schemas']['FulfillmentJob']`, re-exported by
// services/orders.ts) — a backend contract change now fails `tsc` here
// instead of silently rendering `undefined` (#1405). The former inline
// interface duplicated it with four off-wire fields (`attempt`,
// `last_error`, `batch_signature`, `completion_idempotency_key`) whose
// columns rendered `undefined`; those columns are deleted below.
// Re-exported so sibling dispatch modules keep their import path.
export type { FulfillmentJob } from '@/services';
import type { FulfillmentJob } from '@/services';

/**
 * The order-label pair lives in `./widgets.tsx` — two cells need it and so does
 * `detail.title`, and the widget module is the one of the two that may not
 * import the other (it would drag `http` into every test's module graph, see
 * that file's header). Re-exported here so the public import path is unchanged.
 */
export { formatJobOrderLabel, resolveJobOrderName } from './widgets';

/**
 * The whitelist is the GENERATED `FULFILLMENT_JOB_FILTERS` (codegen from the
 * backend route's `filterable` via openapi `x-filterable`) — never a
 * hand-written mirror. A mirror silently under-registers: `name` shipped in
 * the backend whitelist while the local copy did not list it, so
 * `toApiListParams` dropped every name condition before it reached the wire
 * and `deriveQueryColumnsFromFields` never offered the filter at all (#1641).
 *
 * Two consequences of that whitelist the default/preset predicates below must
 * respect (#1154): the delivery date the operator sees (`delivery_date` on
 * the wire) IS the job's `shipping_date` column (`buildJobOrderContext`
 * returns it verbatim), so date predicates query `shipping_date` — NOT
 * `delivery_date`, a projection alias the kernel cannot filter; and
 * "active only" is expressed as `status in` rather than `status ne completed`
 * (the kernel allows only eq/in). The same map is handed to ResourcePage as
 * `filterable`, so drift surfaces as a visible warning, not a false filtered
 * view.
 */

const jobAPI = {
  // Repointed to the new backend list endpoint (`GET /api/fulfillment-jobs`,
  // #935). `toApiListParams` translates the table state to the list kernel's
  // query (`limit`→`size`, filters whitelisted against the generated
  // `FULFILLMENT_JOB_FILTERS`). The endpoint returns the `{ items, total,
  // page, size }` body; the api-client unwraps the `data` envelope and this
  // adapter renames the kernel's `size` to the `limit` the shared
  // `ResourcePage` consumes (#1909, #2167 PR-2).
  list: async (params: DataTableParams): Promise<ListResult<FulfillmentJob>> =>
    toListResult(
      await http.get<ListPage<FulfillmentJob>>('/api/fulfillment-jobs', {
        params: toApiListParams(params, FULFILLMENT_JOB_FILTERS),
      }),
    ),
  // Powers the `dispatch/jobs/:id` route (#1202). Repointed to the
  // delivery read surface's single-job endpoint (`GET
  // /api/fulfillment-jobs/{id}`, read-routes `jobDetailRoute`), whose
  // `data` envelope the api-client unwraps into the flat `FulfillmentJob`
  // wire the shared `ResourceDetailPanel` renders.
  detail: (id: string): Promise<FulfillmentJob> =>
    http.get<FulfillmentJob>(`/api/fulfillment-jobs/${encodeURIComponent(id)}`),
};

// SOT mirror of the backend `FULFILLMENT_STATUS` closed set
// (`contexts/delivery/domain/state-machine.ts`) — all SIX lifecycle
// states, in backend order. This drives BOTH the status column label
// renderer AND the derived `ACTIVE_STATUS_VALUES` whitelist, so any state
// missing here is invisible to labels AND silently dropped from the
// active-only filter. `in_progress` (Onfleet taskStarted — driver en
// route) was the omission that re-introduced the #1154 false filtered
// view: an in-flight job vanished from the default / today / tomorrow
// views the moment the driver started delivering.
export const jobStatusOptions = [
  { value: 'planned', label: 'job.enum.planned', color: 'secondary' },
  { value: 'dispatched', label: 'job.enum.dispatched', color: 'primary' },
  { value: 'in_progress', label: 'job.enum.in_progress', color: 'primary' },
  { value: 'completed', label: 'job.enum.completed', color: 'success' },
  { value: 'exception', label: 'job.enum.exception', color: 'destructive' },
  { value: 'cancelled', label: 'job.enum.cancelled', color: 'destructive' },
];

// "Active" = every status except the terminal `completed`, derived from
// the status SOT so a new status is included automatically (no magic
// string list to drift). Used by the default filter and the presets to
// express "hide completed jobs" via the whitelisted `in` op (the kernel
// rejects `ne`, so `status ne completed` would be dropped — #1154).
const ACTIVE_STATUS_VALUES = jobStatusOptions.map((s) => s.value).filter((v) => v !== 'completed');

export function buildShippingDateConditions(date: string): Condition[] {
  return [
    {
      id: 'shipping-date-active-status',
      field: 'status',
      op: 'in',
      value: ACTIVE_STATUS_VALUES,
    },
    {
      id: 'shipping-date',
      field: 'shipping_date',
      op: 'eq',
      value: date,
    },
  ];
}

// #2774 — "past-date" = the delivery date has passed and the job is neither
// completed nor cancelled (owner ruling 2026-10-21: exception counts).
// Derived from the status SOT like ACTIVE_STATUS_VALUES; the test pins the
// exact four values so a new status forces a decision.
export const PAST_DATE_STATUS_VALUES = ACTIVE_STATUS_VALUES.filter((v) => v !== 'cancelled');

// Strictly before `today`: the list kernel's `to` is INCLUSIVE (`<=`,
// backend platform/db/list-filter.ts) and `lt` is not whitelisted for
// shipping_date, so the bound is yesterday.
export function buildPastDateConditions(today: string): Condition[] {
  return [
    { id: 'past-date-status', field: 'status', op: 'in', value: PAST_DATE_STATUS_VALUES },
    {
      id: 'past-date-shipping-date',
      field: 'shipping_date',
      op: 'to',
      value: addCalendarDays(today, -1),
    },
  ];
}

// The count drawn on the past-date preset button. Same predicate, same
// serializer, same endpoint as the preset itself, so the number equals the
// list total the click shows. Keyed under `queryKeys.dispatchJobs` so every
// dispatch invalidation refreshes it; the date in the key rolls it at merchant
// midnight. The date comes from the kit's `useToday()`, the same day store the
// list controller hands to the preset, so the count and the list stay on the
// same day (#2780). A failed request shows no number, never the last cached one.
export function PastDatePresetCount() {
  const today = useToday();
  const { data, isError } = useQuery({
    queryKey: [...queryKeys.dispatchJobs.all(), 'past-date-count', today],
    queryFn: () =>
      jobAPI.list({
        page: 1,
        limit: 1,
        sort: 'created_at:desc',
        extraParams: serializeConditionsToParams(buildPastDateConditions(today), {}),
      }),
    staleTime: 60 * 1000,
  });
  const total = isError ? 0 : (data?.total ?? 0);
  if (total <= 0) return null;
  // The bare number would join the label as "Past date7"; assistive
  // technology reads the hidden phrase (with its own separator) instead.
  return (
    <>
      <Badge variant="secondary" className="ml-1.5" aria-hidden="true">
        {total}
      </Badge>
      <span className="sr-only">{i18n.t('job.preset.pastDateCount', { count: total })}</span>
    </>
  );
}

// The list banner as a component, so `useToday()` runs inside it: `listBanner`
// itself is called conditionally during `ResourcePage` render and must not
// call hooks. Subscribing to the kit's day store moves both cards to the new
// day at merchant midnight (#2780).
function JobListBanner() {
  const today = useToday();
  return (
    <>
      <TodayDeliveryCard date={today} />
      <CutoffBanner date={today} />
    </>
  );
}

/**
 * Delivery-job list filtered to one board cell: the stage's job statuses on
 * that shipping date. Always explicit conditions, never a preset — a preset
 * would replace the stage's status filter with its own.
 */
export function getFulfillmentJobsStageTarget(date: string, stage: BoardStage): string {
  const conditions: Condition[] = [
    { id: 'stage-status', field: 'status', op: 'in', value: [...STAGE_JOB_STATUSES[stage]] },
    { id: 'shipping-date', field: 'shipping_date', op: 'eq', value: date },
  ];
  const params = new URLSearchParams(serializeConditionsToParams(conditions, {}));
  return `/dispatch/jobs?${params.toString()}`;
}

function formatFallbackOrderId(row: FulfillmentJob): string {
  // String context (an i18n interpolation), so `IdCell` can't be used —
  // but the shortening rule must be the same one it applies (#1978).
  return shortenId(row.order_id, LIST_TRUNCATE).text;
}

// ─── bulk schedule-now (#1624) ─────────────────────────────────────────────

interface BulkScheduleResult {
  dispatched: number;
  failed: number;
}

// Same single-job command the detail toolbar fires, looped over the
// selection. SERIAL on purpose: each call creates an Onfleet task, and a
// parallel fan-out would only thundering-herd the Onfleet API. The server
// re-guards every row (planned-only, sibling coverage, CAS), so a stale
// selection degrades to a counted failure — never a mis-dispatch — and one
// rejected row must not abort the rows behind it.
const scheduleNowAPI = {
  scheduleAll: async (jobIds: string[]): Promise<BulkScheduleResult> => {
    let dispatched = 0;
    let failed = 0;
    for (const id of jobIds) {
      try {
        await http.post(`/api/fulfillment-jobs/${encodeURIComponent(id)}/schedule-manually`);
        dispatched += 1;
      } catch {
        // Counted and surfaced in the result toast; per-row diagnosis goes
        // through the detail page, which shows the server's rejection reason.
        failed += 1;
      }
    }
    return { dispatched, failed };
  },
};

const isScheduleEligible = (row: FulfillmentJob): boolean => row.status === 'planned';

const formatScheduleResult = (result: unknown): string => {
  const r = result as BulkScheduleResult;
  return i18n.t('job.bulk.schedule.result', {
    dispatched: r.dispatched,
    failed: r.failed,
  });
};

// ─── bulk cancel (#2795) ───────────────────────────────────────────────────

interface BulkCancelResult {
  cancelled: number;
  // cancel-dispatched answered 409 `onfleet_task_assigned`: the driver has
  // accepted, the driver task stands and the job stays dispatched.
  assigned: number;
  failed: number;
}

// One closed table drives both halves of the bulk cancel: a status is
// cancellable exactly when it names an endpoint here, and each row's status
// picks its endpoint. The backend re-guards both (`wrong_state` 409), so a
// stale row degrades to a counted failure, never a wrong cancellation.
const CANCEL_PATH_BY_STATUS: Partial<Record<FulfillmentJob['status'], string>> = {
  planned: 'cancel',
  dispatched: 'cancel-dispatched',
};

const isCancelEligible = (row: FulfillmentJob): boolean =>
  CANCEL_PATH_BY_STATUS[row.status] !== undefined;

// SERIAL on purpose (#1733): a dispatched row is an outbound driver-task
// deletion, and a parallel fan-out would only thundering-herd that API. One
// rejected row must not abort the rows behind it.
const cancelAPI = {
  cancelAll: async (rows: FulfillmentJob[]): Promise<BulkCancelResult> => {
    let cancelled = 0;
    let assigned = 0;
    let failed = 0;
    for (const row of rows) {
      const path = CANCEL_PATH_BY_STATUS[row.status];
      if (path === undefined) {
        // Unreachable from the bulk bar (it grays an ineligible selection);
        // counted rather than posted if a caller bypasses it.
        failed += 1;
        continue;
      }
      try {
        await http.post(`/api/fulfillment-jobs/${encodeURIComponent(row.id)}/${path}`);
        cancelled += 1;
      } catch (err) {
        // Per-row diagnosis goes through the detail page, which shows the
        // server's rejection reason.
        if (isOnfleetAssignedConflict(err)) assigned += 1;
        else failed += 1;
      }
    }
    return { cancelled, assigned, failed };
  },
};

const formatCancelResult = (result: unknown): string => {
  const r = result as BulkCancelResult;
  return i18n.t('job.bulk.cancel.result', {
    cancelled: r.cancelled,
    assigned: r.assigned,
    failed: r.failed,
  });
};

const f = createFieldConstructors<FulfillmentJob>();

export const jobModel = defineModel<FulfillmentJob>()({
  name: 'job',
  idField: 'id',
  // What an operator calls a job. `recName` has no consumer on the detail
  // surface yet — the header title travels on the host, see
  // `packages/resource-kit-controllers/react/bundle.ts` — so this is the value
  // Stage B resolves to.
  recName: 'name',
  // The ledger row this resource writes to; its one consumer is the
  // change-history tab. See the file header.
  auditResourceType: 'fulfillment_jobs',
  fields: {
    name: f.text({ label: 'job.field.name', widget: 'jobName' }),
    id: f.id({ label: 'job.field.id' }),
    order_id: f.text({ label: 'job.field.orderId', widget: 'jobOrder' }),
    delivery_date: f.date({ label: 'job.field.deliveryDate' }),
    // Filter-only mirror of the delivery date. The wire `delivery_date` is a
    // projection alias the list kernel CANNOT filter; the whitelisted, indexed
    // column is `shipping_date` (see FULFILLMENT_JOB_FILTERS + the today/tomorrow presets,
    // which build `shipping_date` conditions). Without a field carrying that
    // filter id, the QueryBuilder can't resolve the preset chip's label and
    // falls back to the raw wire name ("shipping_date eq 2026-07-14") instead
    // of a localized chip (#1232). Hidden from the list table (delivery_date
    // already renders the date) and absent from every form tab, so it
    // surfaces ONLY as a resolvable filter column.
    shipping_date: f.date({ label: 'job.field.shippingDate' }),
    status: f.enum({
      label: 'job.field.status',
      options: jobStatusOptions,
      widget: 'jobStatusRoute',
    }),
    // Who is delivering it. The NAME is what an operator reads; the id is the
    // filterable column (`driver_id` is whitelisted eq/in — see
    // FULFILLMENT_JOB_FILTERS), which is why both are declared: a name column
    // the backend cannot filter on and a filter column nobody should read.
    driver_name: f.text({ label: 'job.field.driverName' }),
    driver_id: f.text({ label: 'job.field.driverId' }),
    onfleet_task_id: f.text({ label: 'job.field.onfleetTaskId', widget: 'jobOnfleetTaskId' }),
    // Surfaced in the list (no `columnInvisible`): the 4-char short id is the
    // handle operators actually type into the Onfleet UI, so showing it is the
    // whole point of the denormalization.
    onfleet_task_short_id: f.text({
      label: 'job.field.onfleetTaskShortId',
      widget: 'jobOnfleetTaskShortId',
    }),
    // #1291 — Shopify fulfillment id is cross-referenced in the Shopify admin;
    // copyable. It stays `text`, and the widget borrows only the id DISPLAY:
    // re-typing it `id` would move the CSV from `'' → -` to `'' → ''`. See
    // `./widgets.tsx`.
    shopify_fulfillment_id: f.text({
      label: 'job.field.shopifyFulfillmentId',
      widget: 'jobCopyableId',
    }),
    // #1405: the former `attempt` / `last_error` / `completion_idempotency_key`
    // / `batch_signature` columns are gone — the `/api/fulfillment-jobs` wire
    // (generated FulfillmentJob) never emitted them, so they always rendered
    // `undefined` / a dash.
    customer_name: f.text({ label: 'job.field.customerName' }),
    customer_phone: f.text({ label: 'job.field.customerPhone', widget: 'jobCustomerPhone' }),
    delivery_address: f.text({ label: 'job.field.deliveryAddress', widget: 'jobAddress' }),
    original_address: f.text({ label: 'job.field.originalAddress', widget: 'jobAddress' }),
    task_note: f.text({ label: 'job.field.taskNote', widget: 'jobWrappedNote' }),
    order_note: f.text({ label: 'job.field.orderNote' }),
    operator_note: f.text({ label: 'job.field.operatorNote', widget: 'jobWrappedNote' }),
    // A RAW-DATA view, not a packing checklist (ADR-0046 decisions 3 and 6).
    // For a job with a 配送单清单 the backend lists every order line as
    // stored, in order, and marks each object line with `_job_quantity`, this
    // job's 配送单清单 quantity (0 when the line has no id or the list does
    // not hold it). No date rule and no min(list, current), so a drift
    // between the stored list and the order stays visible here. A job whose
    // 配送单清单 is NULL (an old job) keeps the old view: only the lines dated
    // for its delivery date, by STRICT equality, with no mark.
    //
    // The display filters live in the other readers: the `task_note` above,
    // and the delivery job printout (this list's `print` capability), where
    // the packing truth lives.
    //
    // The LABEL states the scope and nothing more — the order's items, with
    // this job's quantity — because a label is a NAME, not a paragraph slot: this
    // string also renders as a one-line menu entry in the column picker
    // (`components/ui/ListToolbar.tsx` paints `col.label` from
    // `useColumnVisibility`'s `toggleableColumns`, which this field joins via
    // `jobListView.items`). It is NOT in the QueryBuilder field list —
    // `line_items_json` is absent from `FULFILLMENT_JOB_FILTERS`, and
    // `deriveQueryColumnsFromFields` drops whatever the whitelist omits.
    // The rest of the reasoning above lives in code, next to the projection
    // that causes it (`delivery/app/order-fields.ts`) and in
    // `backend/src/__tests__/contexts/delivery/detail-vs-printout-scope-1896.test.ts`,
    // which asserts the view for both kinds of job. There is deliberately no new
    // contract member for it: neither the field spec (`ModelField`) nor the
    // group spec (`FormLeaf { kind: 'fields' }`) carries prose, and the legacy
    // `EntityFieldBase.description` has exactly two runtime readers, both out of
    // reach here — the checkbox caption in
    // `packages/resource-kit-ui/react/registry/field-type-registry.tsx` (json
    // is not boolean) and `packages/resource-kit-ui/react/form/FieldSlot.tsx`,
    // which paints it only in `mode === 'edit'`, while this resource has no
    // write path.
    // Copy pinned by `__tests__/jobResource.lineItemsScope.test.ts`.
    line_items_json: f.json({ label: 'job.field.lineItemsJson', widget: 'jobLineItems' }),
    created_at: f.datetime({ label: 'job.field.createdAt' }),
    updated_at: f.datetime({ label: 'job.field.updatedAt' }),
  },
});

export const jobListView = defineListView(jobModel, {
  items: [
    // The default columns (#2680), first and in reading order, so the table
    // and the phone card show them the same way. The rest keep their order.
    { field: 'name' },
    { field: 'customer_phone' },
    { field: 'delivery_address' },
    { field: 'status' },
    { field: 'delivery_date' },
    { field: 'driver_name' },
    { field: 'onfleet_task_short_id' },
    // id is detail-only; the `name` column supersedes it in the list view.
    { field: 'id', columnInvisible: true },
    { field: 'order_id', columnInvisible: true },
    // Filter-only, on BOTH surfaces. `columnInvisible` keeps it out of the
    // table; `detailInvisible` keeps it off the record, where it would render
    // the SAME value as `delivery_date` above under a near-identical label.
    { field: 'shipping_date', columnInvisible: true, detailInvisible: true },
    { field: 'onfleet_task_id', columnInvisible: true },
    // Filter-only, like `shipping_date` above: the QueryBuilder needs a field
    // carrying the whitelisted filter id to render a resolvable chip, but the
    // raw UUID has no business in the table — nor on the record, where
    // `driver_name` in the `fulfillment` group is the same driver, readable.
    { field: 'driver_id', columnInvisible: true, detailInvisible: true },
    { field: 'shopify_fulfillment_id', columnInvisible: true },
    { field: 'customer_name', columnInvisible: true },
    { field: 'original_address', columnInvisible: true },
    { field: 'task_note', columnInvisible: true },
    { field: 'order_note', columnInvisible: true },
    { field: 'operator_note', columnInvisible: true },
    { field: 'line_items_json', columnInvisible: true },
    { field: 'created_at', columnInvisible: true },
    { field: 'updated_at', columnInvisible: true },
  ],
  defaultSort: { field: 'created_at', dir: 'desc' },
  capabilities: {
    // `/api/fulfillment-jobs` really searches seven sources: order number,
    // Onfleet short id, operator note, order note, line items (#1642, #2069),
    // customer name and customer phone, the phone by digits only (#2678) —
    // see FULFILLMENT_JOB_SEARCH in filters.generated.ts. Pasting the
    // full displayed job name (`#87297HGS-0729`) works: the store strips the
    // derived `-MMDD` suffix before matching the stored order number.
    search: true,
    // The checkbox column the two bulk commands select into.
    select: true,
    // The delivery-job printout lives here now, as this list's print
    // capability, rather than as a page of its own (`/packaging`).
    print: true,
  },
});

/** Read-only list: the drawer shows the same columns; there is no write path. */
export const jobFormView = defineFormView(jobModel, {
  // #2775: four tabs, in the owner's order. Each tab but the third groups
  // titled sections inline; a titled child is a section heading, an untitled
  // one (progress, audit) is a bare block.
  groupDisplay: 'tab',
  children: [
    {
      kind: 'group',
      id: 'main',
      label: 'job.group.main',
      groupDisplay: 'inline',
      children: [
        // #2677: first, so the detail page opens on "when does it arrive".
        { kind: 'custom', id: 'progress', render: DeliveryProgress },
        {
          kind: 'fields',
          id: 'basic',
          label: 'job.section.basic',
          items: [
            { field: 'name' },
            { field: 'order_id' },
            { field: 'delivery_date' },
            { field: 'status' },
          ],
        },
        {
          kind: 'fields',
          id: 'driver',
          label: 'job.section.driver',
          items: [
            { field: 'driver_name' },
            { field: 'onfleet_task_id' },
            { field: 'onfleet_task_short_id' },
            { field: 'shopify_fulfillment_id' },
          ],
        },
      ],
    },
    {
      kind: 'group',
      id: 'customer',
      label: 'job.group.customer',
      groupDisplay: 'inline',
      children: [
        {
          kind: 'fields',
          id: 'recipient',
          label: 'job.section.recipient',
          // `original_address` is declared here only (#1922): the kit does
          // not de-duplicate across groups, and the raw value reads best
          // beside the cleaned `delivery_address`.
          items: [
            { field: 'customer_name' },
            { field: 'customer_phone' },
            { field: 'delivery_address' },
            { field: 'original_address' },
          ],
        },
        {
          kind: 'fields',
          id: 'notes',
          label: 'job.section.notes',
          items: [{ field: 'task_note' }, { field: 'order_note' }, { field: 'operator_note' }],
        },
      ],
    },
    {
      kind: 'fields',
      id: 'data',
      label: 'job.group.data',
      items: [{ field: 'line_items_json' }],
    },
    {
      kind: 'group',
      id: 'history',
      label: 'resource.changeHistory',
      groupDisplay: 'inline',
      children: [
        {
          kind: 'fields',
          id: 'record',
          label: 'job.section.record',
          items: [{ field: 'id' }, { field: 'created_at' }, { field: 'updated_at' }],
        },
        // Declared, not derived (#2783): the kit no longer appends a history tab.
        { kind: 'audit', id: 'audit' },
      ],
    },
  ],
});

export const jobResource: ResourceBundle<FulfillmentJob, typeof jobModel.fields> = {
  model: jobModel,
  list: jobListView,
  form: jobFormView,
  host: {
    api: { list: jobAPI.list, get: jobAPI.detail },
    label: 'job.label',
    slug: 'dispatch/jobs',
    listTitle: 'job.listTitle',
    listDescription: 'job.listDescription',
    // The phone card (#2680): order name and date as the title.
    listCard: renderJobCard,
    // `.v2` (#2680, owner decision C): a one-time reset of every operator's
    // stored jobs columns and layout, so the new default columns are seen.
    // `DispatchJobList` reads the same key.
    listPreferenceKey: 'dispatch/jobs.v2',
    // Single source for the `dispatch/jobs` route guard (#1152) — App.tsx
    // derives the PermissionGuard from here instead of a parallel literal.
    permission: { list: PERMISSIONS.DISPATCH_READ, read: PERMISSIONS.DISPATCH_READ },
    /*
     * The cut-off notice, from the retired `/packaging` page. It belongs
     * wherever the operator prepares a print run: "this job changed after
     * cut-off" is precisely the thing that must be seen BEFORE labels go on
     * boxes.
     *
     * Pinned to TODAY in the merchant timezone rather than following the
     * list's filters. The banner reports orders amended after today's
     * cut-off — a fact about today's packing, not about whatever slice of
     * jobs happens to be on screen. (The old page tied it to its own date
     * picker; this list has no single date to tie to.)
     *
     * The today's-deliveries card (#2588) sits above it and is pinned to
     * TODAY for the same reason: it counts today's delivery jobs by status,
     * whatever the list's filters show. Both read the same date, from the
     * kit's `useToday()`.
     */
    listBanner: () => <JobListBanner />,
    /*
     * The delivery-job printout (CONTEXT.md: 配送单打印) — the same delivery
     * job seen on paper, not a second entity, which is why it is a view of
     * THIS list rather than its own page.
     *
     * The card contents cannot come from the rows this list already holds:
     * the job read face is a raw-data view (the whole order for a job with a
     * 配送单清单; only the dated lines, dropping undated ones, for a job whose
     * 配送单清单 is NULL), and the packing face applies the display filters
     * and keeps undated items, deliberately (#1694 / #1706 / #1716). So the
     * sheet is fetched by job id from `/api/packaging`, which is the only
     * place that divergence is implemented.
     *
     * Gated on `packaging:export` — the grant that used to guard the CSV and
     * HTML downloads. Printing is what that permission now names.
     */
    print: {
      permission: PERMISSIONS.PACKAGING_EXPORT,
      options: [
        {
          kind: 'boolean',
          id: 'pageBreakPerDriver',
          labelKey: 'packaging.card.perDriverPageBreak',
        },
      ],
      page: PACKAGING_PRINT_PAGE,
      buildSheet: ({ rows, options }) =>
        buildPackagingSheet(
          rows.map((row) => row.id),
          options.pageBreakPerDriver === true,
        ),
    },
    // Bulk commands carry a disabledReason because the bulk bar grays an
    // action when ANY selected row is ineligible.
    resourceBulkActions: [
      {
        id: 'schedule-now',
        label: 'job.bulk.schedule.label',
        permission: PERMISSIONS.DISPATCH_WRITE,
        intent: 'default',
        confirm: {
          title: 'job.bulk.schedule.confirm.title',
          description: 'job.bulk.schedule.confirm.description',
        },
        disabledReason: 'job.bulk.schedule.disabledReason',
        isEligible: isScheduleEligible,
        onAction: (rows) => scheduleNowAPI.scheduleAll(rows.map((r) => r.id)),
        formatResult: formatScheduleResult,
      },
      {
        id: 'cancel',
        label: 'job.bulk.cancel.label',
        permission: PERMISSIONS.DISPATCH_WRITE,
        intent: 'danger',
        confirm: {
          title: 'job.bulk.cancel.confirm.title',
          description: 'job.bulk.cancel.confirm.description',
          // The action's own label is "Cancel"; a generic "Cancel" dismiss
          // button would mean both "do it" and "back out".
          cancelText: 'job.bulk.cancel.confirm.keep',
        },
        disabledReason: 'job.bulk.cancel.disabledReason',
        isEligible: isCancelEligible,
        onAction: (rows) => cancelAPI.cancelAll(rows),
        formatResult: formatCancelResult,
      },
    ],
    // Cross-checked by ResourcePage so any preset/default condition the
    // backend would drop surfaces a visible warning (#1154).
    filterable: FULFILLMENT_JOB_FILTERS,
    sortable: FULFILLMENT_JOB_SORTS,
    /*
     * Filtering by driver used to mean pasting an internal UUID (#2068). The
     * roster is small enough to enumerate — production held 40 drivers when
     * this was written, against a list page cap of 100 — so the whole set is
     * fetched once and injected as the column's options. If the roster ever
     * passes 100 this silently truncates and the filter needs a searching
     * control instead.
     *
     * Deliberately unfiltered by `active`: a filter saved against a driver who
     * has since left must still read as their name, not a bare id.
     *
     * `job.field.driverName` ("Driver"), not `driverId` ("Driver ID") — the
     * operator is picking a person now. `driver_name` is not in the backend
     * filter whitelist, so this cannot collide with a second driver column.
     */
    filterOptions: [
      {
        fieldId: 'driver_id',
        label: 'job.field.driverName',
        load: async () => {
          const page = await driverAPI.list({ page: 1, limit: 100, sort: 'name:asc' });
          // `d.id` is the internal driver UUID the job column stores — NOT
          // `d.onfleet_id`, which addresses the same person in Onfleet.
          return page.items.map((d) => ({ value: d.id, label: d.name }));
        },
      },
    ],
    defaultConditions: [
      // Active-only baseline via the whitelisted `in` op — `ne` is dropped
      // by the kernel, which would leak completed jobs into the view (#1154).
      { id: 'default-active', field: 'status', op: 'in', value: ACTIVE_STATUS_VALUES },
    ],
    // #575 — operator-facing quick filters for the two highest-frequency
    // dispatch queries ("which active jobs are due today / tomorrow?").
    // #2774 adds "past-date": past delivery date, not completed, not cancelled.
    // The kit passes `buildConditions` the merchant-tz calendar day and calls
    // it again when that day changes (#2780). Both predicates use ONLY backend-whitelisted field/ops so
    // they actually reach the server (#1154): the date filter targets the
    // `shipping_date` column (the wire `delivery_date` IS that value), and
    // the active-only filter uses `status in` (not the kernel-rejected
    // `ne`). The same active-status predicate that drives `defaultConditions`
    // is repeated here so the preset acts as a STRICTER filter on top of the
    // active-only baseline, rather than accidentally widening the view to
    // completed jobs scheduled for today/tomorrow (which would defeat the
    // operator's intent).
    presets: [
      {
        id: 'today',
        label: 'job.preset.today',
        buildConditions: (today) => buildShippingDateConditions(today),
      },
      {
        id: 'tomorrow',
        label: 'job.preset.tomorrow',
        buildConditions: (today) => buildShippingDateConditions(addCalendarDays(today, 1)),
      },
      {
        id: 'past_date',
        label: 'job.preset.pastDate',
        buildConditions: (today) => buildPastDateConditions(today),
        adornment: () => <PastDatePresetCount />,
      },
    ],
    searchPlaceholder: 'job.searchPlaceholder',
    staleTime: 60 * 1000,
    detail: {
      title: (item) =>
        i18n.t('job.detail.title', {
          label: item.name ?? formatJobOrderLabel(item) ?? formatFallbackOrderId(item),
        }),
      // #1291 — lifecycle status badge next to the job title in the detail header.
      statusField: 'status',
      // Pre-#345: passed raw wire enum (`planned` / `exception` / etc.)
      // into the i18n interpolation, leaking the wire form into the
      // operator-facing header. Route through the SOT `jobStatusOptions`
      // so unknown statuses fail LOUD via the helper. EQD §3.
      subtitle: (item) =>
        i18n.t('job.detail.subtitle', {
          status: lookupEnumLabelOrWarn(item.status, jobStatusOptions, 'job status'),
          date: item.delivery_date || i18n.t('job.detail.noDate'),
        }),
      // Per-job command affordances (#1268/#1525). Lifecycle commands are
      // status-scoped; operator-note editing remains available in every state.
      // `leaveDetail` lets a command that removes the job leave the detail
      // (#2793, #2802).
      headerActions: (item, { leaveDetail }) => (
        <JobActionsToolbar job={item} leaveDetail={leaveDetail} />
      ),
    },
  },
};
